import { Badge, Button, Select, Spin, Tag } from "antd"
import { FormProps } from "antd/es/form/Form"
import React, { FC, useContext, useEffect, useMemo, useState } from "react"
import {
    CourseListFilters,
    DayFilter,
    MonthFilter,
    FiltersInput,
    GetLocationsPublicQuery,
    SettingEndUserAppQuery,
    useCourseGetInstructorsPublicQuery,
    useGetCourseTypesPublicQuery,
    useGetLocationsPublicQuery,
    useSettingEndUserAppQuery,
    useGetCourseCategoriesPublicQuery,
    useGetCourseListWithPaginationPublicQuery,
} from "../../generated/graphql"
import LayoutForm from "../../views/LayoutForm/LayoutForm"
import useWindowDimensions from "../../layouts/Main/GetWindowDimensions/GetWindowDimensions"
import { mediaSizes } from "../../styles/layout"
import { useTranslation } from "react-i18next"
import css from "./CourseFilters.module.css"
import { InCommingAttributes } from "../../types/filters"
import { SettingsContext } from "../../layouts/Main/MainLayout"
import classNames from "classnames"
import MobileFilterScreen, { FilterFieldDescriptor } from "./MobileFilterScreen"
import { parentScrollToOffset, parentAutoResize } from "../../helpers/parentIFrame"
import { getActiveFilterFields } from "./filterActivity"

export const useCourseFilters = () => {}

type CourseFilterProps = {
    otherSettings?: Partial<InCommingAttributes>
    courseFilterValues?: (e: CourseListFilters) => void
    courseFilters?: CourseListFilters
    setCourseFilters?: React.Dispatch<React.SetStateAction<CourseListFilters>>
    incommingFilters: InCommingAttributes
    // Nur Interessenten-Listen (listType=interest): Kursleiter und Kursstart-Monat gibt es dort nicht.
    interestOnly?: boolean
    style?: React.CSSProperties
}

// SHARED HELPERS (Modul-Scope) -----------------------------------------------------------------------------------
// Diese Helfer wurden aus dem CourseFilters-Body herausgezogen, damit die unten definierten
// Filter-Komponenten auf Modul-Ebene leben können (kein Remount mehr bei jedem Eltern-Render).

const defaultOption = (filterName?: string) => {
    // const key = uuid()
    const key = undefined // otherwise the filter will take the uuid to be the value
    return (
        <Select.Option key={key} value={undefined}>
            {`Alle ${filterName}`}
        </Select.Option>
    )
}

const defaultAll = (filterName?: string) => {
    return "Alle " + filterName
}

const getFilterIsActiveClass = (courseFilters: CourseListFilters, filter: keyof CourseListFilters) => {
    const isActive = courseFilters[filter] !== null && courseFilters[filter] !== undefined
    const filterClassNames = isActive ? classNames(css.input, css.active) : classNames(css.input)

    return filterClassNames
}

// Englisch -> Deutsch für die Monatsanzeige (auf Modul-Ebene, damit auch die
// aktiven Filter-Chips dieselbe Übersetzung nutzen können).
const monthTranslation: { [MonthFilterKeys: string]: string } = {
    January: "Januar",
    February: "Februar",
    March: "März",
    April: "April",
    May: "Mai",
    June: "Juni",
    July: "Juli",
    August: "August",
    September: "September",
    October: "Oktober",
    November: "November",
    December: "Dezember",
}

// Gemeinsame Props der einfachen, datengetriebenen Filter (lesen courseFilters, schreiben über setCourseFilters)
type FilterProps = {
    courseFilters: CourseListFilters
    setCourseFilters: React.Dispatch<React.SetStateAction<CourseListFilters>>
}

// Props der Filter, die auf die im Parent geladenen Locations und das geteilte loading angewiesen sind
type LocationAwareFilterProps = FilterProps & {
    locations: GetLocationsPublicQuery["locationsPublic"]
    loading: boolean
}

// Props der Filter, die nur das geteilte loading benötigen (eigene Datenquellen sind statisch)
type LoadingAwareFilterProps = FilterProps & {
    loading: boolean
}

type ChipDataSources = {
    courseTypes: Array<{ id: string; name: string }>
    instructors: Array<{ id: string; firstname: string; lastname: string }>
    categories: Array<{ id: string; name: string }>
    locations: GetLocationsPublicQuery["locationsPublic"]
}

// Menschlich lesbares Label für einen aktiven Filter. Liefert null, solange ein
// ID-basierter Wert (noch) nicht aufgelöst werden kann -> dann wird kein Chip mit
// roher UUID angezeigt.
const chipLabelForField = (field: keyof CourseListFilters, value: unknown, sources: ChipDataSources): string | null => {
    const toArray = (v: unknown) => (Array.isArray(v) ? v : [v])
    switch (field) {
        case "courseTypeId":
            return sources.courseTypes.find((c) => c.id === value)?.name ?? null
        case "instructorId": {
            const instructor = sources.instructors.find((x) => x.id === value)
            return instructor ? `${instructor.firstname} ${instructor.lastname}` : null
        }
        case "locationId":
            return sources.locations.find((l) => l.id === value)?.name ?? null
        case "courseCategoryId":
            return sources.categories.find((c) => c.id === value)?.name ?? null
        case "city":
            return value ? String(value) : null
        case "dayFilter":
            return (
                toArray(value)
                    .filter(Boolean)
                    .join(", ") || null
            )
        case "monthFilter":
            return (
                toArray(value)
                    .filter(Boolean)
                    .map((m) => monthTranslation[m as string] ?? String(m))
                    .join(", ") || null
            )
        default:
            return value !== null && value !== undefined ? String(value) : null
    }
}

// FILTER-KOMPONENTEN (Modul-Scope) -----------------------------------------------------------------------------

const CourseTypeFilter: FC<FilterProps> = ({ courseFilters, setCourseFilters }) => {
    const { data, loading } = useGetCourseTypesPublicQuery()
    const courseTypesFromQuery = data?.courseTypesPublic
    // Memoized: ohne useMemo entstünde bei jedem Render eine neue Array-Referenz,
    // die den Invalidierungs-Effekt unten unnötig erneut feuern lässt.
    const sortedCourseTypes = useMemo(() => {
        const courseTypes = courseTypesFromQuery ?? []
        return courseTypes.slice().sort((a, b) => {
            return a.name.localeCompare(b.name)
        })
    }, [courseTypesFromQuery])

    const selectedCourseCategoryId = courseFilters.courseCategoryId
    const filteredCourseTypes = useMemo(() => {
        return selectedCourseCategoryId
            ? sortedCourseTypes.filter((courseType) => {
                  return courseType.category?.id === selectedCourseCategoryId
              })
            : sortedCourseTypes
    }, [sortedCourseTypes, selectedCourseCategoryId])

    // console.log("selectedCourseCategoryId", selectedCourseCategoryId)
    // console.log("count of filteredCourseTypes", filteredCourseTypes.length)

    const onChange = (value: string) => {
        setCourseFilters((prev) => ({ ...prev, courseTypeId: value }))
    }

    // Extrahiere courseTypeId als primitive Variable
    const courseTypeId = courseFilters.courseTypeId

    useEffect(() => {
        // Nur invalidieren, wenn die Kurstyp-Liste wirklich final geladen ist. Während eines
        // Refetch/Remounts kann sie kurzzeitig leer/unvollständig sein – dann würde eine gültige
        // Auswahl fälschlich zurückgesetzt (Ursache des "Filter springt nach Sekunden zurück"-Bugs).
        if (loading || sortedCourseTypes.length === 0) return
        // Wenn die ausgewählte KursTypeID nicht in den gefilterten Types existiert, zurücksetzen.
        // Gilt auch bei LEERER gefilterter Liste (z.B. ungültige courseCategoryId): dann kann
        // keine courseTypeId gültig sein und ein gesetzter Filter muss ebenfalls zurück.
        if (courseTypeId) {
            const isValid = filteredCourseTypes.some((type) => type.id === courseTypeId)
            if (!isValid) {
                setCourseFilters((prev) => ({ ...prev, courseTypeId: null }))
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, sortedCourseTypes.length, filteredCourseTypes, courseTypeId])

    return (
        <Select
            data-testid='course-name-filter'
            showSearch
            filterOption={(input, option) => {
                return option?.children ? option.children.toString().toLowerCase().includes(input.toLowerCase()) : false
            }}
            onChange={onChange}
            loading={loading}
            value={courseTypeId}
            allowClear
            className={getFilterIsActiveClass(courseFilters, "courseTypeId")}
            defaultValue={defaultAll("Kursnamen")}
        >
            {defaultOption("Kursnamen")}
            {filteredCourseTypes.map((courseType) => {
                return (
                    <Select.Option key={courseType.id} value={courseType.id}>
                        {courseType.name}
                    </Select.Option>
                )
            })}
        </Select>
    )
}

const InstructorsFilter: FC<FilterProps> = ({ courseFilters, setCourseFilters }) => {
    const { data, loading } = useCourseGetInstructorsPublicQuery()
    const instructors = data?.instructorsPublic ?? []

    const onChange = (value: string) => {
        setCourseFilters((prev) => ({ ...prev, instructorId: value }))
    }

    return (
        <Select
            showSearch
            filterOption={(input, option) => (option?.children ? option.children.toString().toLowerCase().includes(input.toLowerCase()) : false)}
            onChange={onChange}
            loading={loading}
            value={courseFilters.instructorId}
            allowClear
            className={getFilterIsActiveClass(courseFilters, "instructorId")}
            defaultValue={defaultAll("Kursleiter")}        >
            {defaultOption("Kursleiter")}
            {instructors.map((instructor) => {
                const fullName = `${instructor.firstname} ${instructor.lastname}`
                return (
                    <Select.Option key={instructor.id} value={instructor.id}>
                        {fullName}
                    </Select.Option>
                )
            })}
        </Select>
    )
}

const CityFilter: FC<LocationAwareFilterProps> = ({ courseFilters, setCourseFilters, locations, loading }) => {
    const cities: Array<string> = []

    locations.forEach((location) => {
        cities.push(location.city)
    })

    const uniqueCities = Array.from(new Set(cities)).sort((a, b) => a.localeCompare(b))

    const onChange = (value: string) => {
        // Stadtwechsel nullt zugleich die locationId (Semantik unverändert).
        setCourseFilters((prev) => ({ ...prev, city: value, locationId: null }))
    }

    return (
        <Select
            showSearch
            onChange={onChange}
            loading={loading}
            value={courseFilters.city}
            allowClear
            className={getFilterIsActiveClass(courseFilters, "city")}
            defaultValue={defaultAll("Städte")}        >
            {defaultOption("Städte")}
            {uniqueCities.map((city) => {
                return <Select.Option key={city} value={city}>{`${city}`}</Select.Option>
            })}
        </Select>
    )
}

const LocationFilter: FC<LocationAwareFilterProps> = ({ courseFilters, setCourseFilters, locations, loading }) => {
    let cityLocations: GetLocationsPublicQuery["locationsPublic"] = locations
    const onChange = (value: string) => {
        setCourseFilters((prev) => ({ ...prev, locationId: value }))
    }

    if (courseFilters.city) {
        const locationsOfCity: GetLocationsPublicQuery["locationsPublic"] = []
        locations.forEach((location) => {
            if (courseFilters.city === location.city) {
                locationsOfCity.push(location)
            }
        })
        cityLocations = locationsOfCity
    }

    return (
        <Select
            showSearch
            filterOption={(input, option) => (option?.children ? option.children.toString().toLowerCase().includes(input.toLowerCase()) : false)}
            onChange={onChange}
            loading={loading}
            value={courseFilters.locationId}
            allowClear
            className={getFilterIsActiveClass(courseFilters, "locationId")}
            defaultValue={defaultAll("Veranstaltungsorte")}        >
            {defaultOption("Veranstaltungsorte")}
            {cityLocations.map((location) => {
                return (
                    <Select.Option key={location.id} value={location.id}>
                        {`${location.name}`}
                    </Select.Option>
                )
            })}
        </Select>
    )
}

const DaysFilter: FC<LoadingAwareFilterProps> = ({ courseFilters, setCourseFilters, loading }) => {
    const mapedDaysFilter = Object.values(DayFilter).map((value) => {
        return { [value]: DayFilter[value] }
    })

    const onChange = (value: CourseListFilters["dayFilter"]) => {
        setCourseFilters((prev) => ({ ...prev, dayFilter: value }))
    }

    return (
        <Select
            mode='multiple'
            showSearch
            onChange={onChange}
            loading={loading}
            value={courseFilters.dayFilter}
            allowClear
            className={getFilterIsActiveClass(courseFilters, "dayFilter")}
            placeholder='Alle Wochentage'        >
            {mapedDaysFilter.map((day, index) => {
                let key = ""
                let value = ""

                Object.entries(day).forEach(([oKey, oValue]) => {
                    key = oKey
                    value = oValue
                })

                return <Select.Option key={key} value={mapedDaysFilter[index][value]}>{`${value}`}</Select.Option>
            })}
        </Select>
    )
}

const MonthsFilter: FC<LoadingAwareFilterProps> = ({ courseFilters, setCourseFilters, loading }) => {
    const mapedMonthFilter = Object.values(MonthFilter).map((value) => {
        return { [value]: MonthFilter[value] }
    })

    const onChange = (value: CourseListFilters["monthFilter"]) => {
        setCourseFilters((prev) => ({ ...prev, monthFilter: value }))
    }

    return (
        <Select
            onChange={onChange}
            loading={loading}
            value={courseFilters.monthFilter}
            allowClear
            className={getFilterIsActiveClass(courseFilters, "monthFilter")}
            // @ts-ignore
            defaultValue={defaultAll("Monate")}        >
            {defaultOption("Monate")}
            {mapedMonthFilter.map((month, index) => {
                let key = ""
                let value = ""

                Object.entries(month).forEach(([oKey, oValue]) => {
                    key = oKey
                    value = oValue
                })

                return <Select.Option key={key} value={mapedMonthFilter[index][value]}>{`${monthTranslation[value]}`}</Select.Option>
            })}
        </Select>
    )
}

const CourseCategoryFilter: FC<FilterProps> = ({ courseFilters, setCourseFilters }) => {
    const { data, loading } = useGetCourseCategoriesPublicQuery()
    const courseCategories = data?.courseCategoriesPublic ?? []
    const sortedCourseCategories = courseCategories.slice().sort((a, b) => {
        return a.name.localeCompare(b.name)
    })

    const onChange = (value: string) => {
        setCourseFilters((prev) => ({ ...prev, courseCategoryId: value }))
    }

    return (
        <Select
            showSearch
            filterOption={(input, option) => {
                return option?.children ? option.children.toString().toLowerCase().includes(input.toLowerCase()) : false
            }}
            onChange={onChange}
            loading={loading}
            value={courseFilters.courseCategoryId}
            allowClear
            className={getFilterIsActiveClass(courseFilters, "courseCategoryId")}
            defaultValue={defaultAll("Kurskategorien")}        >
            {defaultOption("Kurskategorien")}
            {sortedCourseCategories.map((courseCategory) => {
                return (
                    <Select.Option key={courseCategory.id} value={courseCategory.id}>
                        {courseCategory.name}
                    </Select.Option>
                )
            })}
        </Select>
    )
}

// Entfernbare Chips der aktiv gesetzten Filter (Mobile, über der Kursliste).
// Wirkt auf den COMMITTED-State (setCourseFilters) -> Entfernen wendet sofort an.
type ActiveFilterChipsProps = {
    courseFilters: CourseListFilters
    setCourseFilters: React.Dispatch<React.SetStateAction<CourseListFilters>>
    incommingFilters: InCommingAttributes
    locations: GetLocationsPublicQuery["locationsPublic"]
    removeLabel: string
}

const ActiveFilterChips: FC<ActiveFilterChipsProps> = ({ courseFilters, setCourseFilters, incommingFilters, locations, removeLabel }) => {
    // Apollo-gecached: dieselben Queries wie in den Filter-Selects, daher kein
    // zusätzlicher Netzwerk-Request, nur ID -> Name Auflösung für die Chip-Labels.
    const { data: courseTypesData } = useGetCourseTypesPublicQuery()
    const { data: instructorsData } = useCourseGetInstructorsPublicQuery()
    const { data: categoriesData } = useGetCourseCategoriesPublicQuery()

    const sources: ChipDataSources = {
        courseTypes: courseTypesData?.courseTypesPublic ?? [],
        instructors: instructorsData?.instructorsPublic ?? [],
        categories: categoriesData?.courseCategoriesPublic ?? [],
        locations,
    }

    const baseline = incommingFilters as CourseListFilters
    const activeFields = getActiveFilterFields(courseFilters, baseline)

    const removeField = (field: keyof CourseListFilters) => {
        setCourseFilters((prev) => {
            const next: CourseListFilters = { ...prev, [field]: baseline[field] ?? undefined }
            // Stadt entfernen nullt zugleich die locationId (Kaskade wie im CityFilter).
            if (field === "city") {
                next.locationId = baseline.locationId ?? null
            }
            return next
        })
    }

    const chips = activeFields
        .map((field) => ({ field, label: chipLabelForField(field, courseFilters[field], sources) }))
        .filter((chip): chip is { field: keyof CourseListFilters; label: string } => chip.label !== null)

    if (chips.length === 0) return null

    return (
        <div className={css.chipsBar} data-testid='active-filter-chips'>
            {chips.map(({ field, label }) => (
                <Tag
                    key={field}
                    closable
                    className={css.chip}
                    closeIcon={
                        <span aria-label={removeLabel} role='img'>
                            ×
                        </span>
                    }
                    onClose={(e: React.MouseEvent<HTMLElement>) => {
                        e.preventDefault()
                        removeField(field)
                    }}
                >
                    {label}
                </Tag>
            ))}
        </div>
    )
}

// Konzept-2-Vollbild-Filteransicht (mobile, < md). true = neues Redesign aktiv;
// false schaltet Mobile auf den Inline-/Desktop-Fallback zurück (Rollback-Toggle).
const MOBILE_FILTER_REDESIGN_ENABLED = true

const CourseFilters: FC<CourseFilterProps> = ({
    otherSettings,
    courseFilterValues,
    courseFilters: controlledCourseFilters,
    setCourseFilters: controlledSetCourseFilters,
    incommingFilters,
    interestOnly,
    style,
}) => {
    const { t } = useTranslation()
    const showFilter = (filterName: string) => !(interestOnly && (filterName === "instructor" || filterName === "month"))

    // Synchroner Mobile-Guard: useWindowDimensions liest window.innerWidth bereits
    // im useState-Initializer (im Gegensatz zu useMediaSize, das im ersten Frame
    // immer "xs" liefert). So gibt es auf dem Desktop kein kurzes Aufblitzen des
    // mobilen Drawers. "media === 'xs'" entspricht hier width < md (768).
    const windowWidth = useWindowDimensions().width
    const isMobile = windowWidth !== null && windowWidth < mediaSizes.md

    // Controlled-or-internal: liegen die controlled Props (vom Context-gestützten
    // Container) vor, sind sie die Single Source of Truth. Ohne sie (z.B. im
    // Behavior-Test) fällt die Komponente auf eigenen internen State zurück.
    const [internalFilters, setInternalFilters] = useState<CourseListFilters>(incommingFilters)
    const courseFilters = controlledCourseFilters ?? internalFilters
    const setCourseFilters = controlledSetCourseFilters ?? setInternalFilters

    const { data, loading } = useGetLocationsPublicQuery()
    const locations: GetLocationsPublicQuery["locationsPublic"] = data?.locationsPublic ?? []

    // Stammdaten für die Mobile-Drill-in-Optionen. skip:!isMobile -> auf Desktop wird
    // KEIN Request gefeuert (dort liefern die Filter-Selects ihre Daten selbst); so
    // bleibt der Desktop-Pfad inkl. Query-/Timing-Verhalten exakt unverändert. Auf
    // Mobile Apollo-gecached (dieselben Queries wie ActiveFilterChips) -> kein Extra-Request.
    const { data: courseTypesData, loading: courseTypesLoading } = useGetCourseTypesPublicQuery({ skip: !isMobile })
    const { data: instructorsData } = useCourseGetInstructorsPublicQuery({ skip: !isMobile })
    const { data: categoriesData } = useGetCourseCategoriesPublicQuery({ skip: !isMobile })

    // SETTINGS FOR THE END_USER_APP -------------------------------------------------------------------------------------

    const appSettingsFromContext = useContext(SettingsContext)

    const [appSettings, setAppSettings] = useState<SettingEndUserAppQuery["settingEndUserAppPublic"]>(appSettingsFromContext)

    const { data: appSettingsData, loading: loadingAppSettings, error: errorAppSettings } = useSettingEndUserAppQuery()

    useEffect(() => {
        if (appSettingsData) {
            setAppSettings(appSettingsData.settingEndUserAppPublic)
            courseFilterValues?.(courseFilters)
        }
    }, [appSettingsData, loadingAppSettings, errorAppSettings, courseFilterValues, courseFilters])

    // MOBILE DRAWER (Draft/Apply) ---------------------------------------------------------------------------------------
    // Auf Mobile schreiben die Selects im Drawer in einen lokalen Draft; committet
    // wird erst bei "Anzeigen". Desktop bleibt unverändert Live-Apply.
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [draftFilters, setDraftFilters] = useState<CourseListFilters>(courseFilters)

    const openDrawer = () => {
        setDraftFilters(courseFilters) // Draft frisch aus dem committed State seeden
        setDrawerOpen(true)
        // Progressive Enhancement (Variante A): nur wirksam, wenn der Embed parentIFrame
        // freigibt (enablePublicMethods); in Produktion aktuell nie -> defensive Helfer.
        parentScrollToOffset(0, 0) // Modulkopf in den Eltern-Viewport
        parentAutoResize(false) // gemeldete Höhe einfrieren
    }
    const closeDrawer = () => {
        setDrawerOpen(false) // Abbrechen/Backdrop: Draft verwerfen
        parentAutoResize(true) // Höhenmessung freigeben
    }
    const applyDraft = () => {
        setCourseFilters(draftFilters)
        setDrawerOpen(false)
        parentAutoResize(true)
    }
    // Zurücksetzen geht auf die iFrame-Baseline (nicht {}), damit ein vorgegebener
    // locationId nicht gelöscht wird; committet wird weiterhin erst per "Anzeigen".
    const resetDraft = () => setDraftFilters(incommingFilters as CourseListFilters)

    // Live-Trefferzahl für den "Anzeigen"-Button: eigene Pagination-Query gegen den
    // Draft, nur die Gesamtzahl (limit 1). Übersprungen, solange Desktop oder Drawer
    // geschlossen. Reicht die Filter unverändert durch — wie CoursesTable, seit der
    // locationId-Workaround mit ko-X-main#612 entfallen ist.
    const draftCountVariables = useMemo(() => {
        return { options: { limit: 1, offset: 0 }, filters: draftFilters }
    }, [draftFilters])

    const { data: draftCountData, loading: draftCountLoading } = useGetCourseListWithPaginationPublicQuery({
        variables: draftCountVariables,
        fetchPolicy: "cache-and-network",
        skip: !isMobile || !drawerOpen,
    })
    const draftTotal = draftCountData?.coursesWithPaginationPublic?.total

    // Spiegelt die CourseTypeFilter-Cross-Invalidierung für den Draft: passt im Vollbild
    // eine gewählte courseTypeId nicht (mehr) zur gewählten Kategorie, wird sie im Draft
    // zurückgesetzt (sonst "springt" der Filter beim Anzeigen zurück). Auf Mobile rendert
    // CourseTypeFilter nicht, daher hier eigens für den Draft repliziert.
    useEffect(() => {
        if (!isMobile || !drawerOpen || courseTypesLoading) return
        const allTypes = courseTypesData?.courseTypesPublic ?? []
        if (allTypes.length === 0) return
        const { courseTypeId, courseCategoryId } = draftFilters
        if (!courseTypeId) return
        const validTypes = courseCategoryId ? allTypes.filter((type) => type.category?.id === courseCategoryId) : allTypes
        if (!validTypes.some((type) => type.id === courseTypeId)) {
            setDraftFilters((prev) => ({ ...prev, courseTypeId: null }))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMobile, drawerOpen, courseTypesLoading, courseTypesData, draftFilters.courseTypeId, draftFilters.courseCategoryId])

    // LAYOUT ---------------------------------------------------------------------------------------------------------

    type FilterContainerCSSProperties = React.CSSProperties & {
        "--column-count": number
    }

    const actualStyle: FilterContainerCSSProperties = {
        "--column-count": 2,
        ...style,
    }

    const layout: FormProps = {
        labelCol: { xs: 6, sm: 6, md: 6, lg: 6 },
        // wrapperCol: { xs: 2, sm: 2, md: 24, lg: 24 },
        labelAlign: "left",
        layout: "horizontal",
        size: isMobile ? "small" : "middle",
    }

    // LAYOUT ---------------------------------------------------------------------------------------------------------

    if (otherSettings?.showFilterMenu === false) {
        return null
    }

    // CUSTOMER FILTERS --------------------------------------------------------------------------------

    const renderCustomFilters = (filters: CourseListFilters, setFilters: React.Dispatch<React.SetStateAction<CourseListFilters>>) => {
        const filterArray: Array<React.ReactElement> = []

        const allFilters = [
            { name: "courseType", label: "Kursname", component: <CourseTypeFilter courseFilters={filters} setCourseFilters={setFilters} /> },
            { name: "instructor", label: "Kursleiter", component: <InstructorsFilter courseFilters={filters} setCourseFilters={setFilters} /> },
            { name: "city", label: "Stadt", component: <CityFilter courseFilters={filters} setCourseFilters={setFilters} locations={locations} loading={loading} /> },
            { name: "location", label: "Veranstaltungsort", component: <LocationFilter courseFilters={filters} setCourseFilters={setFilters} locations={locations} loading={loading} /> },
            { name: "weekDay", label: "Wochentage", component: <DaysFilter courseFilters={filters} setCourseFilters={setFilters} loading={loading} /> },
            { name: "month", label: "Monat", component: <MonthsFilter courseFilters={filters} setCourseFilters={setFilters} loading={loading} /> },
            { name: "courseCategory", label: "Kurskategorien", component: <CourseCategoryFilter courseFilters={filters} setCourseFilters={setFilters} /> },
        ]

        const customerFilters: FiltersInput = appSettings.filters

        const courseTypeIdsIsSet = incommingFilters.courseTypeIds?.length && incommingFilters.courseTypeIds?.length > 0
        const locationIdIsSet = incommingFilters.locationId !== null && incommingFilters.locationId !== undefined && incommingFilters.locationId !== ""

        if (customerFilters && Object.keys(customerFilters).length > 0) {
            Object.keys(customerFilters).filter(showFilter).forEach((filterName) => {
                switch (filterName) {
                    case "courseType":
                        if (!courseTypeIdsIsSet) {
                            filterArray.push(
                                <div className={css.filter} key={filterName}>
                                    <div className={css.label}>Kursname</div>
                                    <CourseTypeFilter courseFilters={filters} setCourseFilters={setFilters} />
                                </div>
                            )
                        }
                        break
                    case "instructor":
                        filterArray.push(
                            <div className={css.filter} key={filterName}>
                                <div className={css.label}>Kursleiter</div>
                                <InstructorsFilter courseFilters={filters} setCourseFilters={setFilters} />
                            </div>
                        )
                        break
                    case "city":
                        if (!locationIdIsSet) {
                            filterArray.push(
                                <div className={css.filter} key={filterName}>
                                    <div className={css.label}>Stadt</div>
                                    <CityFilter courseFilters={filters} setCourseFilters={setFilters} locations={locations} loading={loading} />
                                </div>
                            )
                        }
                        break
                    case "location":
                        if (!locationIdIsSet) {
                            filterArray.push(
                                <div className={css.filter} key={filterName}>
                                    <div className={css.label}>Veranstaltungsort</div>
                                    <LocationFilter courseFilters={filters} setCourseFilters={setFilters} locations={locations} loading={loading} />
                                </div>
                            )
                        }
                        break
                    case "weekDay":
                        filterArray.push(
                            <div className={css.filter} key={filterName}>
                                <div className={css.label}>Wochentag</div>
                                <DaysFilter courseFilters={filters} setCourseFilters={setFilters} loading={loading} />
                            </div>
                        )
                        break
                    case "month":
                        filterArray.push(
                            <div className={css.filter} key={filterName}>
                                <div className={css.label}>Monat des Kursstarts</div>
                                <MonthsFilter courseFilters={filters} setCourseFilters={setFilters} loading={loading} />
                            </div>
                        )
                        break
                    case "courseCategory":
                        if (!incommingFilters.courseCategoryIds?.length && !incommingFilters.courseCategoryId) {
                            filterArray.push(
                                <div className={css.filter} key={filterName}>
                                    <div className={css.label}>Kurskategorien</div>
                                    <CourseCategoryFilter courseFilters={filters} setCourseFilters={setFilters} />
                                </div>
                            )
                            break
                        } else {
                            break
                        }
                }
            })
        } else {
            allFilters.filter((filter) => showFilter(filter.name)).forEach((filter) => {
                filterArray.push(
                    <div className={css.filter} key={filter.label}>
                        <div className={css.label}>{filter.label}</div>
                        {filter.component}
                    </div>
                )
            })
        }

        return filterArray
    }

    // MOBILE FILTER FIELDS --------------------------------------------------------------------------------
    // Leitet aus DERSELBEN customerFilters-Guard-Logik wie renderCustomFilters (Desktop)
    // die sichtbaren Felder ab, baut daraus aber FieldDescriptors für die Vollbild-Ansicht
    // (i18n-Labels, Drill-in-Optionen, Draft-onChange). Single Source of Truth bleibt die
    // customerFilters-Sichtbarkeit; nur die Darstellung unterscheidet sich von Desktop.
    const getMobileFilterFields = (): FilterFieldDescriptor[] => {
        const customerFilters = appSettings?.filters
        const courseTypeIdsIsSet = incommingFilters.courseTypeIds?.length && incommingFilters.courseTypeIds?.length > 0
        const locationIdIsSet = incommingFilters.locationId !== null && incommingFilters.locationId !== undefined && incommingFilters.locationId !== ""
        const courseCategoryIdIsSet = Boolean(incommingFilters.courseCategoryIds?.length) || Boolean(incommingFilters.courseCategoryId)

        // Optionsquellen (Apollo-gecached); sortiert wie die Desktop-Selects.
        const sortedCourseTypes = (courseTypesData?.courseTypesPublic ?? []).slice().sort((a, b) => a.name.localeCompare(b.name))
        const visibleCourseTypes = draftFilters.courseCategoryId
            ? sortedCourseTypes.filter((type) => type.category?.id === draftFilters.courseCategoryId)
            : sortedCourseTypes
        const instructors = instructorsData?.instructorsPublic ?? []
        const sortedCategories = (categoriesData?.courseCategoriesPublic ?? []).slice().sort((a, b) => a.name.localeCompare(b.name))
        const uniqueCities = Array.from(new Set(locations.map((location) => location.city))).sort((a, b) => a.localeCompare(b))
        const cityLocations = draftFilters.city ? locations.filter((location) => location.city === draftFilters.city) : locations

        const chipSources: ChipDataSources = {
            courseTypes: courseTypesData?.courseTypesPublic ?? [],
            instructors,
            categories: categoriesData?.courseCategoriesPublic ?? [],
            locations,
        }
        const resolveValueText = (field: keyof CourseListFilters) => chipLabelForField(field, draftFilters[field], chipSources)
        const setSingle = (field: keyof CourseListFilters) => (value: string | null) => setDraftFilters((prev) => ({ ...prev, [field]: value }))
        const noop = () => {}

        const descriptors: { [key: string]: FilterFieldDescriptor } = {
            courseType: {
                key: "courseTypeId",
                label: t("courseFilters.fieldLabel.courseName"),
                valueText: resolveValueText("courseTypeId"),
                drill: {
                    mode: "single",
                    title: t("courseFilters.drillTitle.courseName"),
                    options: visibleCourseTypes.map((type) => ({ value: type.id, label: type.name })),
                    selected: draftFilters.courseTypeId ?? null,
                    searchable: true,
                    clearAllLabel: t("courseFilters.clearAll.courseName"),
                    onPickSingle: setSingle("courseTypeId"),
                    onApplyMulti: noop,
                },
            },
            instructor: {
                key: "instructorId",
                label: t("courseFilters.fieldLabel.instructor"),
                valueText: resolveValueText("instructorId"),
                drill: {
                    mode: "single",
                    title: t("courseFilters.drillTitle.instructor"),
                    options: instructors.map((instructor) => ({ value: instructor.id, label: `${instructor.firstname} ${instructor.lastname}` })),
                    selected: draftFilters.instructorId ?? null,
                    searchable: true,
                    clearAllLabel: t("courseFilters.clearAll.instructor"),
                    onPickSingle: setSingle("instructorId"),
                    onApplyMulti: noop,
                },
            },
            city: {
                key: "city",
                label: t("courseFilters.fieldLabel.city"),
                valueText: resolveValueText("city"),
                drill: {
                    mode: "single",
                    title: t("courseFilters.drillTitle.city"),
                    options: uniqueCities.map((city) => ({ value: city, label: city })),
                    selected: draftFilters.city ?? null,
                    searchable: true,
                    clearAllLabel: t("courseFilters.clearAll.city"),
                    // Stadtwechsel nullt zugleich die locationId (Kaskade wie im CityFilter).
                    onPickSingle: (value) => setDraftFilters((prev) => ({ ...prev, city: value, locationId: null })),
                    onApplyMulti: noop,
                },
            },
            location: {
                key: "locationId",
                label: t("courseFilters.fieldLabel.location"),
                valueText: resolveValueText("locationId"),
                drill: {
                    mode: "single",
                    title: t("courseFilters.drillTitle.location"),
                    options: cityLocations.map((location) => ({ value: location.id, label: location.name })),
                    selected: draftFilters.locationId ?? null,
                    searchable: true,
                    clearAllLabel: t("courseFilters.clearAll.location"),
                    onPickSingle: setSingle("locationId"),
                    onApplyMulti: noop,
                },
            },
            weekDay: {
                key: "dayFilter",
                label: t("courseFilters.fieldLabel.day"),
                valueText: resolveValueText("dayFilter"),
                drill: {
                    mode: "multi",
                    title: t("courseFilters.drillTitle.day"),
                    options: Object.values(DayFilter).map((day) => ({ value: day, label: day })),
                    selected: draftFilters.dayFilter ?? [],
                    searchable: false,
                    onPickSingle: noop,
                    onApplyMulti: (values) => setDraftFilters((prev) => ({ ...prev, dayFilter: values as DayFilter[] })),
                },
            },
            month: {
                key: "monthFilter",
                label: t("courseFilters.fieldLabel.month"),
                valueText: resolveValueText("monthFilter"),
                drill: {
                    mode: "multi",
                    title: t("courseFilters.drillTitle.month"),
                    options: Object.values(MonthFilter).map((month) => ({ value: month, label: monthTranslation[month] ?? month })),
                    selected: draftFilters.monthFilter ?? [],
                    searchable: false,
                    onPickSingle: noop,
                    onApplyMulti: (values) => setDraftFilters((prev) => ({ ...prev, monthFilter: values as MonthFilter[] })),
                },
            },
            courseCategory: {
                key: "courseCategoryId",
                label: t("courseFilters.fieldLabel.category"),
                valueText: resolveValueText("courseCategoryId"),
                drill: {
                    mode: "single",
                    title: t("courseFilters.drillTitle.category"),
                    options: sortedCategories.map((category) => ({ value: category.id, label: category.name })),
                    selected: draftFilters.courseCategoryId ?? null,
                    searchable: true,
                    clearAllLabel: t("courseFilters.clearAll.category"),
                    onPickSingle: setSingle("courseCategoryId"),
                    onApplyMulti: noop,
                },
            },
        }

        const order: string[] = []
        if (customerFilters && Object.keys(customerFilters).length > 0) {
            Object.keys(customerFilters).filter(showFilter).forEach((filterName) => {
                switch (filterName) {
                    case "courseType":
                        if (!courseTypeIdsIsSet) order.push("courseType")
                        break
                    case "instructor":
                        order.push("instructor")
                        break
                    case "city":
                        if (!locationIdIsSet) order.push("city")
                        break
                    case "location":
                        if (!locationIdIsSet) order.push("location")
                        break
                    case "weekDay":
                        order.push("weekDay")
                        break
                    case "month":
                        order.push("month")
                        break
                    case "courseCategory":
                        if (!courseCategoryIdIsSet) order.push("courseCategory")
                        break
                }
            })
        } else {
            order.push("courseType", "instructor", "city", "location", "weekDay", "month", "courseCategory")
        }

        return order.filter(showFilter).map((filterName) => descriptors[filterName]).filter(Boolean)
    }

    // RETURN ------------------------------------------------------------------------------------------

    if (isMobile && MOBILE_FILTER_REDESIGN_ENABLED) {
        const activeFields = getActiveFilterFields(courseFilters, incommingFilters as CourseListFilters)
        // Live-Trefferzahl erst zeigen, wenn sie zum aktuellen Draft geladen ist; bei 0
        // Treffern wird der Anzeigen-Button deaktiviert ("Keine Kurse gefunden").
        const hasCount = !draftCountLoading && draftTotal !== null && draftTotal !== undefined
        const applyDisabled = hasCount && draftTotal === 0
        const applyLabel = !hasCount
            ? t("courseFilters.apply")
            : draftTotal === 0
              ? t("courseFilters.empty.applyDisabled")
              : t(draftTotal === 1 ? "courseFilters.applyWithCountOne" : "courseFilters.applyWithCountOther", { count: draftTotal })

        return (
            <div className={css.mobileFilterBar} style={style} data-testid='mobile-filter-bar'>
                <div className={css.mobileTriggerRow}>
                    <Badge count={activeFields.length} size='small' offset={[-2, 4]}>
                        <Button onClick={openDrawer} data-testid='mobile-filter-trigger'>
                            {t("courseFilters.filterButton")}
                        </Button>
                    </Badge>
                </div>
                <ActiveFilterChips
                    courseFilters={courseFilters}
                    setCourseFilters={setCourseFilters}
                    incommingFilters={incommingFilters}
                    locations={locations}
                    removeLabel={t("courseFilters.removeFilter")}
                />
                {drawerOpen && (
                    <MobileFilterScreen
                        title={t("courseFilters.title")}
                        fields={loadingAppSettings ? [] : getMobileFilterFields()}
                        onClose={closeDrawer}
                        onReset={resetDraft}
                        onApply={applyDraft}
                        applyLabel={applyLabel}
                        applyDisabled={applyDisabled}
                        applyLoading={draftCountLoading}
                        resetLabel={t("courseFilters.reset")}
                        closeLabel={t("courseFilters.close")}
                        backLabel={t("courseFilters.back")}
                        selectPlaceholder={t("courseFilters.selectPlaceholder")}
                        searchPlaceholder={t("courseFilters.searchPlaceholder")}
                        searchNoResultsLabel={t("courseFilters.searchNoResults")}
                        applySelectionLabel={t("courseFilters.applySelection")}
                    />
                )}
            </div>
        )
    }

    return (
        <LayoutForm className={css.filterContainer} style={actualStyle} {...layout}>
            {loadingAppSettings ? <Spin /> : renderCustomFilters(courseFilters, setCourseFilters)}
        </LayoutForm>
    )
}

export default CourseFilters
